<template>
  <field-group
      :label="label"
      class="table-fill-section">
    <option-tiles
        :options="fillOptions"
        :value="activeFillType"
        @click="setFillType($event.value)">
      <template
          slot="preview"
          slot-scope="{ option }">
        <none-mark
            v-if="option.isNone"
            slash/>
        <span
            v-else
            :style="option.style"
            class="table-fill-swatch"/>
      </template>
    </option-tiles>

    <info-banner
        v-if="isMixed"
        tone="neutral"
        class="table-fill-hint">
      {{ mixedColorsHint }}
    </info-banner>

    <div
        v-if="showPicker"
        class="table-fill-picker">
      <color-picker
          :key="pickerKey"
          :color="pickerColor"
          :selected-objects-list="selectedObjectsList"
          :content-list="contentList"
          color-type="table-fill"
          display-style="inline"
          variant="panel"
          @color-changed="onColorChanged"
      />
    </div>
  </field-group>
</template>

<script>
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import OptionTiles from '@/components/LeftSection/ui/OptionTiles';
import InfoBanner from '@/components/LeftSection/ui/InfoBanner';
import NoneMark from '@/components/LeftSection/ui/NoneMark';
import {
  TABLE_FILL_NONE,
  TABLE_FILL_COLOR,
  applyFillToCells,
  cloneFill,
  getCellFill,
  normalizeFill,
  sharedCellsFill,
  tableCellCoords,
} from '@/components/DesignObjects/TableObject';

export default {
  name:       'TableFillSection',
  components: {
    'color-picker': ColorPicker,
    'field-group':  FieldGroup,
    'option-tiles': OptionTiles,
    'info-banner':  InfoBanner,
    'none-mark':    NoneMark,
  },
  props: {
    // The table object out of the content list - written to in place.
    table: {
      type:    Object,
      default: null,
    },
    // 'table' fills every cell, 'cell' fills only the cells in `cells`.
    scope: {
      type:      String,
      default:   'table',
      validator: value => ['table', 'cell'].includes(value),
    },
    cells: {
      type:    Array,
      default: () => [],
    },
    label: {
      type:    String,
      default: 'Background',
    },
    selectedObjectsList: {
      type:    Array,
      default: () => [],
    },
    contentList: {
      type:    [Array, Object],
      default: () => [],
    },
  },
  computed: {
    tableId() {
      return (this.table && this.table.Id) || 'none';
    },
    // The cells this section writes to: the picked ones, or the whole table.
    targetCells() {
      if (!this.table || !this.table.cells) { return []; }
      if (this.scope !== 'cell') { return tableCellCoords(this.table); }

      return (this.cells || []).filter(({ r, c }) => {
        const cell = this.table.cells[r] && this.table.cells[r][c];
        return !!cell && !cell.hidden;
      });
    },
    targetKey() {
      return this.targetCells.map(({ r, c }) => `${r}:${c}`).join(',');
    },
    sharedFill() {
      void (this.table && this.table.fillEpoch);
      return sharedCellsFill(this.table, this.targetCells);
    },
    isMixed() {
      return this.targetCells.length > 1 && !this.sharedFill;
    },
    // What the controls read. On a mixed selection this is the first cell's
    // fill, so picking a type has somewhere sensible to start from.
    currentFill() {
      if (this.sharedFill) { return this.sharedFill; }
      const first = this.targetCells[0];
      return normalizeFill(first ? getCellFill(this.table, first.r, first.c) : null);
    },
    // No type is current while the cells disagree.
    activeFillType() {
      return this.isMixed ? null : this.currentFill.type;
    },
    showPicker() {
      return this.activeFillType === TABLE_FILL_COLOR;
    },
    mixedColorsHint() {
      return this.scope === 'cell'
          ? 'The selected cells use different backgrounds.'
          : 'The cells of this table use different backgrounds.';
    },
    fillOptions() {
      return [
        {
          value: TABLE_FILL_NONE,
          label: 'No fill',
          isNone: true,
          style: {},
        },
        {
          value: TABLE_FILL_COLOR,
          label: 'Color fill',
          style: {backgroundColor: this.currentFill.color},
        },
      ];
    },
    pickerColor() {
      return this.currentFill.color;
    },
    // The picker seeds its colour once, on create, so it is remounted whenever
    // the colour it should be showing belongs to something else.
    pickerKey() {
      return [
        'table-fill',
        this.tableId,
        this.scope,
        this.targetKey,
        this.activeFillType,
      ].join('|');
    },
  },
  methods: {
    setFillType(type) {
      if (type === this.activeFillType) {
        return;
      }
      const next = cloneFill(this.currentFill);

      next.type = type;
      this.commit(next);
    },

    onColorChanged(payload) {
      const rgba = payload && payload.rgba;
      if (!rgba) {
        return;
      }
      const next = cloneFill(this.currentFill);

      next.color = rgba;
      this.commit(next);
    },

    commit(fill) {
      if (!this.table || !this.targetCells.length) {
        return;
      }

      applyFillToCells(
          this.table,
          this.targetCells,
          fill,
          (cell, key, value) => {
            this.$set(cell, key, value);
          },
      );
      this.$set(this.table, 'fillEpoch', (this.table.fillEpoch || 0) + 1);
      this.$emit('change');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './styles/lp-tokens';

// The colour fill swatch drawn inside the "Color fill" tile
.table-fill-swatch {
  width: 24px;
  height: 24px;
  border: 1px solid rgba(20, 22, 27, 0.12);
  border-radius: 6px;
}

.table-fill-hint {
  margin-top: 8px;
}

.table-fill-picker {
  margin-top: 8px;
}
</style>
